import { LockFilled } from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Button, Input, Select, Space } from 'antd';
import { ReactComponent as GreenDot } from 'assets/images/icons/greenDot.svg';
import { ReactComponent as IconBell } from 'assets/images/icons/iconBell.svg';
import { ReactComponent as IconNote } from 'assets/images/icons/iconNote.svg';
import { FormSelect } from 'components/Custom/selects';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AccountType, UpgradeMessageSource } from 'store/api/enums';
import { turnDrawerOn, turnModalOff, turnModalOn } from 'store/app/actions';
import {
  ActivityRoles,
  DataLogStatus,
  DrawerType,
  WindowType,
} from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { User } from 'store/auth/models';
import {
  ActivityLog,
  ActivityRoleUpdateRequest,
  VatRunData,
} from 'store/vat/models';
import styled from 'styled-components';
import { RenderUserDetail } from 'utils/render-user-details';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { ActivityLogs } from './activity-logs';

const ActivityWrapper = styled.div`
  //border-top: 1px solid var(--color-border, #dbeae3);
  display: flex;
  flex-direction: column;
  padding-top: 17px;
  align-items: center;

  .activity-settings-buttons {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .selects-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
  }
  .selects-row {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: flex-start;
    min-width: 0;
  }
  /* Spacers only — the rail is a single centered ::before so it cannot jog. */
  .linethru {
    width: 0;
    flex: 1 1 auto;
    min-height: 8px;
    height: auto;
    background: transparent;
  }
  .linethru-100 {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
  }
  .column-1 {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 10px;
    width: 10px;
    min-width: 10px;
    align-self: stretch;
    line-height: 0;
  }
  .column-1:not(:empty)::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    margin-left: -1px;
    background: var(--color-border, #dbeae3);
    pointer-events: none;
  }
  .column-1.cell-1-1:not(:empty)::before {
    top: 8px;
  }
  .column-1:has(.divider-10)::before {
    bottom: 10px;
  }
  .column-1 svg {
    position: relative;
    z-index: 1;
    display: block;
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
  }
  .cell-1-1 {
    padding-top: 4px;
  }

  .cell-label {
    font-family: Inter;
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.05em;
    padding-left: 12px;
  }

  .cell-select {
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 12px;
    width: auto;
  }
`;

const { TextArea } = Input;
interface ActivityTabProps {
  data: VatRunData;
  updateActivityRole: (
    activityRoleUpdateRequest: ActivityRoleUpdateRequest
  ) => void;
  activityLogs?: ActivityLog[];
  updateNote: (noteText: string, dataLogId: string) => void;
  currentUser: User;
  isClientLocked: boolean;
  /** PWA Return workflow sheet: hide Process/Add note/Activity chrome; stepper dots. */
  sheetMode?: boolean;
}

const WorkflowStepDot: React.FC<{ done?: boolean }> = ({ done }) => (
  <span
    className={`scope-pwa-workflow-step-dot${done ? ' is-done' : ''}`}
    aria-hidden
  />
);

//const { Text, Title } = Typography;
export const ActivityTab: React.FC<ActivityTabProps> = ({
  data,
  updateActivityRole,
  activityLogs,
  updateNote,
  currentUser,
  isClientLocked,
  sheetMode = false,
}) => {
  const pwaMobileNav = usePwaMobileNavEnabled();
  const selectStyle = React.useMemo(
    () => (pwaMobileNav ? { minWidth: 0 } : { minWidth: 300 }),
    [pwaMobileNav]
  );
  /** Open upward + cap height so staff lists don’t clip under the PWA tab bar. */
  const pwaSelectDropdownProps = React.useMemo(
    () =>
      pwaMobileNav
        ? {
            placement: 'topLeft' as const,
            listHeight: 200,
            popupClassName: 'scope-pwa-process-select-dropdown',
            getPopupContainer: () => document.body,
            dropdownStyle: {
              maxHeight: 'min(42vh, 240px)',
              zIndex: 1300,
            },
          }
        : {},
    [pwaMobileNav]
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [editNote, setEditNote] = React.useState(false);
  const [noteText, setNoteText] = React.useState<string>('');

  const handleClientFlowUpgradeModal = () => {
    dispatch(
      turnModalOn({
        title: '',
        type: WindowType.WARNING,
        component: (
          <UpgradeMessage
            source={UpgradeMessageSource.CLIENT_FLOW}
            onClose={() => dispatch(turnModalOff())}
            userAccountType={currentUser?.accountType}
          />
        ),
      })
    );
  };

  const openActivityLogs = () => {
    const drawerData: DrawerData = {
      title: t('organisation.reconciliation-activity'),
      //subtitle: 'Connect one of the below',
      type: DrawerType.ACTIVITY_LOGS,
      component: (
        <ActivityLogs
          data={data}
          activityLogs={activityLogs}
          updateNote={updateNote}
          connectionId={data.tokenId}
          mappingId={data.mappingId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };
  const [selectedPreparer, setSelectedPreparer] = React.useState(
    data.preparerId
  );

  React.useEffect(() => {
    if (!selectedPreparer) setSelectedPreparer(data.preparerId);
    if (data) {
      const isClientApproval =
        currentUser &&
        AccountType[currentUser.accountType] === AccountType.CLIENT &&
        data.status &&
        DataLogStatus[data.status] === DataLogStatus.AWAITING_CLIENT_APPROVAL;

      setEditNote(!!isClientApproval);
    }
  }, [data]);

  const handleChange = (activityRole: ActivityRoles, accountId: string) => {
    activityRole === ActivityRoles.PREPARER && setSelectedPreparer(accountId);
    const memberUser =
      activityRole === ActivityRoles.CLIENT ||
      activityRole === ActivityRoles.REMOVE_CLIENT
        ? data.clients && data.clients.find((c) => c.accountUuid === accountId)
        : data.staff && data.staff.find((s) => s.accountUuid === accountId);

    const request: ActivityRoleUpdateRequest = {
      activityRole,
      memberUser,
      dataLogId: data.dataLogId,
      mappingId: data.mappingId,
    };

    updateActivityRole(request);
  };

  const onNoteChange = (e) => {
    setNoteText(e.target.value);
  };

  const onNoteSave = () => {
    updateNote(noteText, data.dataLogId);
    noteText !== '' && setEditNote(false);
    setNoteText('');
  };
  const preparerDone = Boolean(data.preparerId || selectedPreparer);
  const reviewerDone = Boolean(data.reviewerId);
  const clientDone = Boolean(data.clientId) && !isClientLocked;
  const reviewerPlaceholder = sheetMode
    ? t('vat-report.assign-reviewer', { defaultValue: 'Assign reviewer' })
    : t('vat-report.reviewer-not-set');
  const clientPlaceholder = sheetMode
    ? t('vat-report.assign-client', { defaultValue: 'Assign client' })
    : t('vat-report.client-not-set');
  const renderStepDot = (done: boolean) =>
    sheetMode ? <WorkflowStepDot done={done} /> : <GreenDot />;

  return (
    <ActivityWrapper
      className={[
        pwaMobileNav ? 'scope-pwa-activity-tab' : '',
        sheetMode ? 'scope-pwa-activity-tab--sheet' : '',
      ]
        .filter(Boolean)
        .join(' ') || undefined}
    >
      {!sheetMode ? (
        <div className="activity-settings-buttons">
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginTop: 5,
              marginBottom: 25,
            }}
          >
            {data?.status &&
            DataLogStatus[data?.status] !== DataLogStatus.SUBMITTED &&
            currentUser?.accountType &&
            AccountType[currentUser.accountType] !== AccountType.CLIENT
              ? t('vat-report.process')
              : ''}
          </span>

          <Space>
            {data?.status &&
              DataLogStatus[data?.status] !== DataLogStatus.SUBMITTED &&
              currentUser?.accountType &&
              AccountType[currentUser.accountType] !== AccountType.CLIENT && (
                <ExportButton
                  icon={<IconNote style={{ width: 20, height: 20 }} />}
                  style={{ marginBottom: 20 }}
                  onClick={() => setEditNote(!editNote)} //@ts-ignore
                >
                  {!editNote
                    ? t('common.add-note')
                    : t('organisation.reconciliation-close-note')}
                </ExportButton>
              )}
            {currentUser?.accountType &&
              AccountType[currentUser.accountType] !== AccountType.CLIENT && (
                <ExportButton
                  icon={
                    <IconBell
                      style={{
                        width: 20,
                        height: 20,
                        color: 'var(--color-text-muted, #5a6a6a)',
                      }}
                    />
                  }
                  style={{ marginBottom: 20 }}
                  onClick={openActivityLogs}
                  disabled={
                    AccountType[currentUser.accountType] === AccountType.CLIENT
                  }
                >
                  {t('organisation.reconciliation-activity')}
                </ExportButton>
              )}
          </Space>
        </div>
      ) : null}
      {editNote && (
        <>
          <TextArea rows={4} onChange={onNoteChange} value={noteText} />
          <div className="divider-20" />
          <ScopeButton
            type={'primary'}
            onClick={onNoteSave} //@ts-ignore
            width="100%"
          >
            {t('organisation.reconciliation-save-note')}
          </ScopeButton>
          <div className="divider-20" />
        </>
      )}

      {data?.status && (
        // DataLogStatus[data?.status] !== DataLogStatus.SUBMITTED &&
        <>
          <div className="selects-wrapper">
            {currentUser &&
              AccountType[currentUser.accountType] !== AccountType.CLIENT && (
                <>
                  <div className="selects-row">
                    <div
                      className={`column-1 cell-1-1${
                        preparerDone ? ' is-complete' : ''
                      }`}
                    >
                      {renderStepDot(preparerDone)}
                      <div className="linethru" />
                    </div>
                    <div className="cell-label">{t('vat-report.preparer')}</div>
                  </div>
                  <div className="selects-row">
                    <div
                      className={`column-1 cell-3-1${
                        preparerDone ? ' is-complete' : ''
                      }`}
                    >
                      <div className="linethru linethru-100" />
                    </div>
                    <div className="cell-select">
                      <FormSelect //@ts-ignore
                        width="100%"
                        height="55px"
                        radius={'10px'}
                        bordercolor="var(--color-border, #dbeae3)"
                        onChange={(e: any) =>
                          handleChange(ActivityRoles.PREPARER, e)
                        }
                        defaultValue={data.preparerId}
                        key={data.preparerId ?? 'preparer-unset'}
                        style={selectStyle}
                        placeholder={t('vat-report.preparer-not-set')}
                        disabled={
                          AccountType[currentUser.accountType] ===
                            AccountType.CLIENT ||
                          DataLogStatus[data?.status] ===
                            DataLogStatus.SUBMITTED
                        }
                        showSearch={true}
                        filterOption={(input: any, option: any) =>
                          option?.title
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        {...pwaSelectDropdownProps}
                      >
                        {data.staff &&
                          data.staff.map((member, idx) => (
                            <Select.Option
                              key={idx}
                              title={`${member.firstName} ${member.lastName}`}
                              value={member.accountUuid}
                            >
                              <RenderUserDetail
                                record={member}
                                showOption
                                hideEmail
                              />
                            </Select.Option>
                          ))}
                      </FormSelect>
                    </div>
                  </div>

                  <div className="selects-row">
                    <div
                      className={`column-1 cell-3-1${
                        preparerDone ? ' is-complete' : ''
                      }`}
                    >
                      <div className="linethru" />
                      {renderStepDot(reviewerDone)}
                    </div>
                    <div className="cell-label" style={{ paddingTop: 5 }}>
                      {t('vat-report.reviewer')}
                    </div>
                  </div>
                  <div className="selects-row">
                    <div
                      className={`column-1 cell-3-1${
                        reviewerDone ? ' is-complete' : ''
                      }`}
                    >
                      <div className="linethru linethru-100" />
                    </div>
                    <div className="cell-select">
                      <FormSelect //@ts-ignore
                        width="100%"
                        height="55px"
                        radius={'10px'}
                        bordercolor="var(--color-border, #dbeae3)"
                        placeholder={reviewerPlaceholder}
                        onChange={(e: any) =>
                          e && handleChange(ActivityRoles.REVIEWER, e)
                        }
                        defaultValue={data.reviewerId}
                        key={data.reviewerId ?? 'reviewer-unset'}
                        style={selectStyle}
                        disabled={
                          AccountType[currentUser.accountType] ===
                            AccountType.CLIENT ||
                          DataLogStatus[data?.status] ===
                            DataLogStatus.SUBMITTED
                        }
                        allowClear
                        onClear={() => {
                          data.reviewerId &&
                            handleChange(
                              ActivityRoles.REMOVE_REVIEWER,
                              data.reviewerId
                            );
                        }}
                        showSearch={true}
                        filterOption={(input: any, option: any) =>
                          option?.title
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        {...pwaSelectDropdownProps}
                      >
                        {data.staff &&
                          data.staff.map(
                            (member, idx) =>
                              member.accountUuid !== selectedPreparer && (
                                <Select.Option
                                  key={idx}
                                  title={`${member.firstName} ${member.lastName}`}
                                  value={member.accountUuid}
                                >
                                  <RenderUserDetail
                                    record={member}
                                    showOption
                                    hideEmail
                                  />
                                </Select.Option>
                              )
                          )}
                      </FormSelect>
                    </div>
                  </div>

                  <div className="selects-row">
                    <div
                      className={`column-1${
                        reviewerDone ? ' is-complete' : ''
                      }`}
                    >
                      <div className="linethru" />
                      {renderStepDot(clientDone)}
                      <div className="divider-10" />
                    </div>
                    <StyledTooltip // @ts-ignore
                      title={
                        isClientLocked
                          ? t(
                              'vat-report.unlock-full-workflow-with-client-approval'
                            )
                          : null
                      }
                      placement="bottom"
                      arrow={false}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          className="cell-label"
                          style={{ paddingTop: 5, paddingRight: 10 }}
                        >
                          {t('vat-report.client')}
                        </div>

                        {isClientLocked && (
                          <LockFilled
                            color="var(--color-text-muted, #5a6a6a)"
                            style={{
                              color: 'var(--color-text-muted, #5a6a6a)',
                              cursor: 'pointer',
                            }}
                            onClick={handleClientFlowUpgradeModal}
                          />
                        )}
                      </div>
                    </StyledTooltip>

                    {isClientLocked && !sheetMode && (
                      <Button
                        type="link"
                        style={{ marginInlineStart: 150 }}
                        onClick={handleClientFlowUpgradeModal}
                      >
                        {t('navigation.upgrade')}
                      </Button>
                    )}
                  </div>
                  <div className="selects-row">
                    <div className=" column-1" />

                    <div className="cell-select">
                      <StyledTooltip // @ts-ignore
                        title={
                          isClientLocked
                            ? t(
                                'vat-report.unlock-full-workflow-with-client-approval'
                              )
                            : null
                        }
                        placement="bottom"
                        arrow={false}
                      >
                        <FormSelect //@ts-ignore
                          width="100%"
                          height="55px"
                          radius={'10px'}
                          bordercolor="var(--color-border, #dbeae3)"
                          onChange={(e: any) =>
                            e && handleChange(ActivityRoles.CLIENT, e)
                          }
                          defaultValue={
                            isClientLocked || !data.clientId ? null : data.clientId
                          }
                          key={data.clientId || 'client-unset'}
                          style={selectStyle}
                          placeholder={clientPlaceholder}
                          disabled={
                            AccountType[currentUser.accountType] ===
                              AccountType.CLIENT ||
                            DataLogStatus[data?.status] ===
                              DataLogStatus.SUBMITTED ||
                            isClientLocked
                          }
                          allowClear
                          onClear={() => {
                            data.clientId &&
                              handleChange(
                                ActivityRoles.REMOVE_CLIENT,
                                data.clientId
                              );
                          }}
                          showSearch={true}
                          filterOption={(input: any, option: any) =>
                            option?.title
                              ?.toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          {...pwaSelectDropdownProps}
                        >
                          {data.clients &&
                            data.clients.map((member, idx) => (
                              <Select.Option
                                key={idx}
                                title={`${member.firstName} ${member.lastName}`}
                                value={member.accountUuid}
                              >
                                <RenderUserDetail
                                  record={member}
                                  showOption
                                  hideEmail
                                />
                              </Select.Option>
                            ))}
                        </FormSelect>
                      </StyledTooltip>
                    </div>
                  </div>
                </>
              )}
          </div>
        </>
      )}
    </ActivityWrapper>
  );
};
